import { createSlice } from '@reduxjs/toolkit';
import { fetchUsers } from '../actions/userActions';
import { fetchDesignations } from '../actions/designationActions';
import { UsersState } from '../types/userTypes';

// Function to load the initial state from localStorage
const loadInitialState = (): UsersState => {
  try {
    // Check if the user is authenticated by looking for a valid token (admin OR employee)
    const cookies = document.cookie.split('; ');
    const adminToken = cookies.find(row => row.startsWith('pms_admin_='))?.split('=')[1];
    const employeeToken = cookies.find(row => row.startsWith('pms_employee_='))?.split('=')[1];

    // If either token exists, try to load cached data for initial render.
    // Always reset hasFetched = false so a fresh fetch happens on every session start.
    // This ensures newly added fields (like managerId) are not missed by stale cache.
    if (adminToken || employeeToken) {
      const savedState = localStorage.getItem('users-data');
      if (savedState) {
        return { ...JSON.parse(savedState), hasFetched: false };
      }
    }
  } catch (error) {
    console.error('Failed to load users state from localStorage:', error);
  }

  // Return empty state if no token or no saved data
  return {
    customers: [],
    employees: [],
    designations: [],
    loading: false,
    error: null,
    hasFetched: false,
  };
};

// Function to save state to localStorage
const saveStateToStorage = (state: UsersState) => {
  try {
    localStorage.setItem('users-data', JSON.stringify(state));
  } catch (error) {
    console.error('Failed to save users state to localStorage:', error);
  }
};

// Clear users data from localStorage - will be used on logout
export const clearUsersFromStorage = () => {
  try {
    localStorage.removeItem('users-data');
  } catch (error) {
    console.error('Failed to remove users state from localStorage:', error);
  }
};

const initialState: UsersState = loadInitialState();

const userSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    clearUserData: state => {
      state.customers = [];
      state.employees = [];
      state.designations = [];
      state.error = null;
      state.loading = false;
      state.hasFetched = false; // Reset the fetch flag when clearing data
      clearUsersFromStorage();
    },
    invalidateDesignationsCache: state => {
      // This action forces a refetch of designations data by setting hasFetched to false
      state.hasFetched = false;
      saveStateToStorage(state);
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchUsers.pending, state => {
        console.log('fetchUsers.pending - Setting loading to true');
        state.loading = true;
        state.error = null;
        saveStateToStorage(state);
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        console.log('fetchUsers.fulfilled - Received payload:', action.payload);
        state.loading = false;
        state.hasFetched = true; // Mark as fetched regardless of data received

        if (action.payload && action.payload.customers) {
          state.customers = action.payload.customers;
          console.log('Updated customers in state:', state.customers);
        } else {
          console.log('No customers data in payload - this is normal if database is empty');
        }

        if (action.payload && action.payload.employees) {
          state.employees = action.payload.employees;
          console.log('Updated employees in state:', state.employees);
        } else {
          console.log('No employees data in payload - this is normal if database is empty');
        }

        if (action.payload && action.payload.designations) {
          state.designations = action.payload.designations;
          console.log('Updated designations in state:', state.designations);
        } else {
          console.log('No designations data in payload - this is normal if database is empty');
        }

        // Save the updated state to localStorage
        saveStateToStorage(state);
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        console.log('fetchUsers.rejected - Error:', action.payload);
        state.loading = false;
        state.hasFetched = true; // Mark as fetched even on error to prevent infinite retries
        state.error = action.payload as string;
        saveStateToStorage(state);
      })
      // Handle fetchDesignations action
      .addCase(fetchDesignations.pending, state => {
        console.log('fetchDesignations.pending - Setting loading to true');
        state.loading = true;
        state.error = null;
        saveStateToStorage(state);
      })
      .addCase(fetchDesignations.fulfilled, (state, action) => {
        console.log('fetchDesignations.fulfilled - Received designations:', action.payload);
        state.loading = false;
        state.hasFetched = true;
        
        // Update designations in state
        state.designations = action.payload;
        console.log('Updated designations in state:', state.designations);
        
        // Save the updated state to localStorage
        saveStateToStorage(state);
      })
      .addCase(fetchDesignations.rejected, (state, action) => {
        console.log('fetchDesignations.rejected - Error:', action.payload);
        state.loading = false;
        state.error = action.payload as string;
        saveStateToStorage(state);
      });
  },
});

export const { clearUserData, invalidateDesignationsCache } = userSlice.actions;
export default userSlice.reducer;
